// D3.4b — client-side video thumbnail extraction. Meta requires a thumbnail on
// every video creative (verified empirically 2026-06-08); this grabs the first
// frame of the LOCAL File the AM just selected so they don't have to upload one
// per video slot by hand.
//
// KEY DESIGN POINT: we extract from URL.createObjectURL(file) — the local bytes —
// NOT from the GCS URL after upload. A same-origin blob: URL avoids CORS/canvas-
// taint entirely (a cross-origin GCS <video> would taint the canvas and make
// toBlob throw), and lets extraction run in PARALLEL with the GCS video upload.
//
// Pure DOM, no React. Callers treat any rejection as "fall back to the manual
// thumbnail upload" — extraction failure must never block the video upload.

export type ThumbnailExtractionReason =
  | 'timeout' // video never became seekable within timeoutMs
  | 'video_error' // the <video> element fired 'error' (corrupt file / unsupported codec)
  | 'no_dimensions' // loadeddata fired but videoWidth/Height are 0 (rare codec case)
  | 'canvas_failed'; // 2d context unavailable or toBlob produced nothing

export class ThumbnailExtractionError extends Error {
  constructor(
    message: string,
    public readonly reason: ThumbnailExtractionReason,
  ) {
    super(message);
    this.name = 'ThumbnailExtractionError';
  }
}

export type ExtractVideoThumbnailOptions = {
  seekTime?: number; // seconds; default 0.1 — the frame at exactly 0 is sometimes black
  quality?: number; // JPEG quality 0–1, default 0.85
  timeoutMs?: number; // default 30s — covers slow decode of large files
};

/**
 * Extract a JPEG first-frame thumbnail from a local video File. Resolves a Blob;
 * rejects with a typed ThumbnailExtractionError (the caller falls back to the
 * manual upload). The object URL is ALWAYS revoked, on every path.
 */
export function extractVideoThumbnail(
  file: File,
  opts: ExtractVideoThumbnailOptions = {},
): Promise<Blob> {
  const seekTime = opts.seekTime ?? 0.1;
  const quality = opts.quality ?? 0.85;
  const timeoutMs = opts.timeoutMs ?? 30_000;

  const url = URL.createObjectURL(file);
  const video = document.createElement('video');

  return new Promise<Blob>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(
      () => fail(new ThumbnailExtractionError(`Thumbnail extraction timed out after ${timeoutMs}ms.`, 'timeout')),
      timeoutMs,
    );
    function cleanup() {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      // Detach the source so the element releases the decoder promptly.
      video.removeAttribute('src');
      video.load?.();
    }
    function fail(err: ThumbnailExtractionError) {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    }
    function succeed(blob: Blob) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(blob);
    }

    video.muted = true;
    video.playsInline = true; // iOS Safari: without it, assigning src can trigger fullscreen playback
    video.preload = 'auto';

    video.addEventListener('error', () =>
      fail(new ThumbnailExtractionError('The video could not be decoded for a thumbnail.', 'video_error')),
    );

    video.addEventListener('loadeddata', () => {
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        // Rare codec case: data loaded but no decodable dimensions — the caller
        // falls back to manual upload.
        fail(new ThumbnailExtractionError('The video reported no dimensions.', 'no_dimensions'));
        return;
      }
      // Seek slightly past 0 (frame 0 is sometimes black); clamp to the duration
      // for sub-100ms clips.
      const duration = Number.isFinite(video.duration) ? video.duration : seekTime;
      video.currentTime = Math.min(seekTime, duration);
    });

    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          fail(new ThumbnailExtractionError('No 2d canvas context available.', 'canvas_failed'));
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) succeed(blob);
            else fail(new ThumbnailExtractionError('Canvas produced no image data.', 'canvas_failed'));
          },
          'image/jpeg',
          quality,
        );
      } catch (e) {
        fail(new ThumbnailExtractionError(e instanceof Error ? e.message : 'Canvas draw failed.', 'canvas_failed'));
      }
    });

    video.src = url;
  });
}
